const KEYBOARD_OPEN_TIMEOUT_MS = 2000;
// ignore small viewport changes (URL bar collapse, scrollbars) — only react to the on-screen keyboard
const KEYBOARD_MIN_HEIGHT_PX = 100;
const VISIBLE_MARGIN_PX = 8;

/**
 * How far a scroller must scroll for `element` to clear the on-screen keyboard, or 0 when it is
 * already clear. Clamped so the scroll never carries the element's own top edge out of the visible
 * area, so an element taller than the visible area is revealed only as far as that allows.
 *
 * Exported so callers already handling a `visualViewport` resize themselves can reuse the maths
 * without waiting for the *next* resize the way {@link keepVisibleAboveKeyboard} does.
 */
export function getScrollToClearKeyboard(
	element: HTMLElement,
	viewport: VisualViewport,
	bottomAllowancePx = 0,
): number {
	const { top, bottom } = element.getBoundingClientRect();
	const visibleTop = viewport.offsetTop;
	const visibleBottom = viewport.offsetTop + viewport.height;
	const overflow = bottom + bottomAllowancePx + VISIBLE_MARGIN_PX - visibleBottom;
	if (overflow <= 0) return 0;
	// never push the element's top edge above the visible area while revealing its bottom edge
	const maxScroll = Math.max(top - visibleTop - VISIBLE_MARGIN_PX, 0);
	// The maths is scroller-agnostic: `getBoundingClientRect` and the visual viewport are both in
	// layout coordinates, and scrolling either the document or an inner scrollport by `overflow`
	// moves the element up by the same amount.
	return Math.min(overflow, maxScroll);
}

/**
 * Keeps an element (typically an input plus the error message rendered below it) visible when the
 * on-screen keyboard opens after a focus.
 *
 * Pass `scrollContainer` when the element sits in its own scrollport rather than the document —
 * inside a `position: fixed` modal, for instance, where scrolling the document achieves nothing.
 * Defaults to the window.
 */
export function keepVisibleAboveKeyboard(
	element: HTMLElement,
	{ bottomAllowancePx = 0, scrollContainer }: { bottomAllowancePx?: number; scrollContainer?: HTMLElement | null } = {},
): void {
	const viewport = window.visualViewport;
	if (!viewport) return;
	const initialHeight = viewport.height;

	const cleanup = () => {
		viewport.removeEventListener('resize', onResize);
		clearTimeout(timer);
	};

	const onResize = () => {
		if (initialHeight - viewport.height < KEYBOARD_MIN_HEIGHT_PX) return;
		// `scrollBy` clamps to the available range.
		const top = getScrollToClearKeyboard(element, viewport, bottomAllowancePx);
		if (top > 0) (scrollContainer ?? window).scrollBy({ top });
		cleanup();
	};

	viewport.addEventListener('resize', onResize);
	const timer = setTimeout(cleanup, KEYBOARD_OPEN_TIMEOUT_MS);
}
